import React from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { useAuth } from "./supabase/AuthProvider";

import MainLayout from "./layouts/MainLayout";
import SurveyPage from "./pages/Survey/SurveyPage";
import { SurveyGate } from "./components/SurveyGate";
import LessonLayout from "./layouts/LessonLayout";
// Home page removed - authenticated users redirect to /courses
import Playground from "./pages/experimental/playground/Playground";
import NotFound from "./components/common/NotFound";
import ProtectedRoute from "./routes/ProtectedRoute";
// import { Signup } from "./pages/Auth/signUp/SignUp";
import { SetPassword } from "./pages/Auth/setPassword";
import ResetPassword from "./pages/ResetPassword";
import EnvironmentBanner from "./components/common/EnvironmentBanner";
import NotFoundCourse from "./pages/Course/NotFoundCourse";
import NotFoundLesson from "./pages/Lesson/NotFoundLesson";

// Login page decommissioned — all logins route through app.pedagogue.io
// import { Login } from "./pages/Auth/login/Login";
import CourseIndex from "./pages/Course";
import LessonIndex from "./pages/LessonPlayer";
import CoursesIndex from "./pages/Courses";
import CertificatesIndex from "./pages/Certificates";
import CertificateDetail from "./pages/Certificates/CertificateDetail";
import CertificateNotFound from "./components/Certificate/CertificateNotFound";
import { MasterCourseDetail, LevelDetail } from "./pages/MasterCourses";

import { Verify } from "./pages/Auth/verify/Verify";
import Welcome from "./pages/Welcome/Welcome";
import { ExternalRedirect } from "./components/ExternalRedirect";
import HandoffHandler from "./components/HandoffHandler";
import FeatureGate from "./components/common/FeatureGate";
import { Feature } from "./types/newtypes";
import { installTrialExpiredHandler } from "./utils/trialExpiredHandler";
import { api } from "./utils/api";


// Install the global trial_expired fetch/axios interceptor. Must run AFTER
// all top-of-module imports to satisfy CRA's ESLint `import/first` rule.
installTrialExpiredHandler([api]);

// WordPress marketing site URL - all marketing pages redirect here
const MARKETING_SITE_URL = process.env.REACT_APP_MARKETING_SITE_URL || "https://pedagogue.io";

// App Launchpad URL - profile management is centralized there
const APP_HOME_URL = process.env.REACT_APP_APP_HOME_URL || "https://app.pedagogue.io";

const App: React.FC = () => {
  const { user, loading } = useAuth();
  const environmentLabel = process.env.REACT_APP_ENVIRONMENT_LABEL;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen text-lg font-bold text-gray-800">
        <span>Loading Pedagogue</span>
        <span className="dot-animation">.</span>
        <span className="dot-animation [animation-delay:0.3s]">.</span>
        <span className="dot-animation [animation-delay:0.6s]">.</span>
      </div>
    );
  }

  return (
    <Router>
      <HandoffHandler>
        <EnvironmentBanner label={environmentLabel} />
        <Routes>
          {/* <Route path="/signup" element={<FirebaseUIWrapper />} /> */}
          {/* <Route path="/signup" element={<Signup />} /> */}
          <Route path="/set-password" element={<SetPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/auth/verify" element={<Verify />} />
          <Route path="/welcome" element={<Welcome />} />

          {/* Preserve old purchase links after moving the journey to App Home. */}
          <Route path="/checkout" element={<ExternalRedirect to={`${APP_HOME_URL}/orders/checkout`} preserveParams />} />
          <Route
            path="/pricing"
            element={<ExternalRedirect to={user ? `${APP_HOME_URL}/subscription` : `${MARKETING_SITE_URL}/pricing`} preserveParams />}
          />
          <Route path="/orders/confirmed" element={<ExternalRedirect to={`${APP_HOME_URL}/orders/confirmed`} preserveParams />} />
          <Route path="/orders/error" element={<ExternalRedirect to={`${APP_HOME_URL}/orders/error`} preserveParams />} />
          <Route path="/trial-expired" element={<ExternalRedirect to={`${APP_HOME_URL}/subscription`} preserveParams />} />
          <Route path="/subscription" element={<ExternalRedirect to={`${APP_HOME_URL}/subscription`} preserveParams />} />

          {/* Login decommissioned — redirect to App Launchpad login */}
          <Route path="/login" element={<ExternalRedirect to={`${APP_HOME_URL}/login`} preserveParams />} />

          {/* Home redirects to /courses for authenticated users, WordPress for unauthenticated */}
          <Route
            path="/"
            element={
              user ? (
                <Navigate to="/courses" replace />
              ) : (
                <ExternalRedirect to={MARKETING_SITE_URL} preserveParams />
              )
            }
          />
          <Route
            path="/master-courses/:id"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <MasterCourseDetail />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/master-courses/:masterCourseId/levels/:levelId"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <LevelDetail />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          {/* Courses - redirects to WordPress for unauthenticated, full view for authenticated */}
          <Route
            path="/courses"
            element={
              user ? (
                <ProtectedRoute>
                  <MainLayout>
                    <CoursesIndex />
                  </MainLayout>
                </ProtectedRoute>
              ) : (
                <ExternalRedirect to={MARKETING_SITE_URL} preserveParams />
              )
            }
          />

          {/* Public course overview - redirects to WordPress marketing site */}
          {/* TODO: Once WordPress course pages are ready, redirect to specific course URL */}
          <Route
            path="/courses/:courseId/overview"
            element={<ExternalRedirect to={`${MARKETING_SITE_URL}/courses`} preserveParams />}
          />
          <Route path="/survey/:wave" element={<ProtectedRoute><MainLayout><SurveyPage /></MainLayout></ProtectedRoute>} />
          <Route
            path="/lesson/:lessonId"
            element={
              <ProtectedRoute>
                <LessonLayout>
                  <SurveyGate>
                    <LessonIndex />
                  </SurveyGate>
                </LessonLayout>
              </ProtectedRoute>
            }
          />

          {/* Modules removed in v9 */}

          <Route
            path="/course/:id"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <CourseIndex />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/certificates"
            element={
              <ProtectedRoute>
                <FeatureGate
                  feature={Feature.COURSE_CERTIFICATES}
                  fallback={<Navigate to="/courses" replace />}
                >
                  <MainLayout>
                    <CertificatesIndex />
                  </MainLayout>
                </FeatureGate>
              </ProtectedRoute>
            }
          />
          <Route
            path="/certificates/:certificateId"
            element={
              <ProtectedRoute>
                <FeatureGate
                  feature={Feature.COURSE_CERTIFICATES}
                  fallback={<Navigate to="/courses" replace />}
                >
                  <MainLayout>
                    <CertificateDetail />
                  </MainLayout>
                </FeatureGate>
              </ProtectedRoute>
            }
          />
          <Route
            path="/passport"
            element={<ExternalRedirect to={`${APP_HOME_URL}/auto-handoff?return_to=${encodeURIComponent(`${APP_HOME_URL}/passport`)}`} />}
          />
          <Route
            path="/Dashboard"
            element={
              <ProtectedRoute>
                <Navigate to="/courses" replace />
              </ProtectedRoute>
            }
          />
          {/* Profile centralized in App Launchpad — redirect for backward compat */}
          <Route path="/profile" element={<ExternalRedirect to={`${APP_HOME_URL}/profile`} />} />
          <Route path="/playground" element={<Playground />} />
          <Route path="/courseNotFound" element={<NotFoundCourse />} />
          <Route path="/certificateNotFound" element={<CertificateNotFound />} />
          <Route path="/lessonNotFound/:course" element={<NotFoundLesson />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </HandoffHandler>
    </Router>
  );
};

export default App;
